import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import Navigation from './Navigation';
import { useAuth } from '../contexts/AuthContext';
import { API_URL } from '../utils/config';
import './SharedStyles.css';

interface LinkResult {
  success: boolean;
  message?: string;
  error?: string;
  flyconan_user_id?: string;
  flyconan_email?: string;
  linked_at?: string;
}

const FlyConanLinkAccount: React.FC = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get('token') || searchParams.get('linking_token');
  const { getAuthHeaders } = useAuth();

  const [loading, setLoading] = useState<boolean>(true);
  const [result, setResult] = useState<LinkResult | null>(null);

  useEffect(() => {
    if (!token) {
      setLoading(false);
      setResult({
        success: false,
        error: 'Missing linking token. Please initiate account linking directly from your FlyConan dashboard.',
      });
      return;
    }

    const linkAccount = async () => {
      try {
        setLoading(true);
        const response = await fetch(`${API_URL}/auth/link-flyconan`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            ...getAuthHeaders(),
          },
          credentials: 'include',
          body: JSON.stringify({ linking_token: token }),
        });

        const data: LinkResult = await response.json();
        setResult(data);
      } catch (err: any) {
        setResult({
          success: false,
          error: err?.message || 'An unexpected error occurred while linking your FlyConan account.',
        });
      } finally {
        setLoading(false);
      }
    };

    linkAccount();
  }, [token, getAuthHeaders]);

  return (
    <div className="page-container page-with-nav">
      <Navigation />
      
      <div style={{ maxWidth: '620px', margin: '0 auto', width: '100%', padding: '0 1rem' }}>
        <div className="card glass-card fade-in" style={{ textAlign: 'center', padding: '2.5rem 2rem' }}>
          
          {/* Dual Brand Integration Header */}
          <div
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '1rem',
              background: 'rgba(255, 255, 255, 0.7)',
              padding: '0.75rem 1.5rem',
              borderRadius: '50px',
              border: '1px solid rgba(122, 107, 53, 0.25)',
              boxShadow: '0 4px 15px rgba(0, 0, 0, 0.04)',
              marginBottom: '1.75rem',
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', color: '#7A6B35', fontWeight: 600, fontSize: '0.9rem' }}>
              <i className="fas fa-plane-departure flyconan-brand-icon" style={{ fontSize: '1.1rem' }}></i>
              <span style={{ color: '#7A6B35' }}>FlyConan</span>
            </div>

            <div
              style={{
                width: '34px',
                height: '34px',
                borderRadius: '50%',
                background: 'linear-gradient(135deg, #7A6B35 0%, #9A8B55 100%)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                boxShadow: '0 2px 8px rgba(122, 107, 53, 0.35)',
              }}
            >
              <i className="fas fa-link link-badge-icon" style={{ fontSize: '0.9rem' }}></i>
            </div>

            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', color: '#1f2937', fontWeight: 600, fontSize: '0.9rem' }}>
              <img src="/icon.png" alt="Forensic AI" style={{ width: '22px', height: '22px', objectFit: 'contain' }} />
              <span style={{ color: '#7A6B35' }}>Forensic AI</span>
            </div>
          </div>

          <h1 className="page-title" style={{ fontSize: '1.85rem', marginBottom: '0.5rem', color: '#0F0F0F' }}>
            Account Integration
          </h1>
          <p className="page-subtitle" style={{ fontSize: '0.95rem', marginBottom: '2rem', color: '#5F6360' }}>
            Connecting your FlyConan operator profile with Forensic Engineering AI Engine
          </p>

          {/* Loading State */}
          {loading && (
            <div style={{ padding: '2.5rem 1rem', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '1.25rem' }}>
              <div
                style={{
                  width: '44px',
                  height: '44px',
                  border: '3px solid rgba(122, 107, 53, 0.2)',
                  borderTopColor: '#7A6B35',
                  borderRadius: '50%',
                  animation: 'spin 0.9s cubic-bezier(0.68, -0.55, 0.27, 1.55) infinite',
                }}
              />
              <div style={{ fontWeight: 600, color: '#0F0F0F', fontSize: '1rem' }}>
                Verifying Inter-Service Auth Token
              </div>
              <p style={{ color: '#5F6360', fontSize: '0.875rem', margin: 0 }}>
                Validating cryptographic signatures with FlyConan issuer...
              </p>
            </div>
          )}

          {/* Success State */}
          {!loading && result?.success && (
            <div className="slide-in" style={{ marginBottom: '2rem' }}>
              <div
                className="message message-success"
                style={{
                  textAlign: 'left',
                  borderRadius: '16px',
                  padding: '1.5rem',
                  border: '1px solid #bbf7d0',
                }}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginBottom: '0.75rem' }}>
                  <i className="fas fa-check-circle" style={{ fontSize: '1.4rem', color: '#16a34a' }}></i>
                  <h3 style={{ margin: 0, fontSize: '1.1rem', fontWeight: 700, color: '#166534' }}>
                    Account Successfully Linked!
                  </h3>
                </div>
                
                <p style={{ margin: '0 0 1.25rem 0', color: '#15803d', fontSize: '0.925rem', lineHeight: 1.5 }}>
                  {result.message || 'Your FlyConan identity is now securely linked with your Forensic AI account.'}
                </p>

                <div
                  style={{
                    background: 'rgba(255, 255, 255, 0.95)',
                    borderRadius: '12px',
                    padding: '1rem 1.25rem',
                    border: '1px solid rgba(22, 163, 74, 0.2)',
                    display: 'flex',
                    flexDirection: 'column',
                    gap: '0.6rem',
                    fontSize: '0.875rem',
                    color: '#1e293b',
                  }}
                >
                  <div style={{ display: 'flex', justifyContent: 'space-between', borderBottom: '1px solid #f1f5f9', paddingBottom: '0.4rem' }}>
                    <span style={{ color: '#64748b', fontWeight: 500 }}>FlyConan User ID</span>
                    <strong style={{ fontFamily: 'monospace', color: '#0f172a' }}>{result.flyconan_user_id}</strong>
                  </div>
                  {result.flyconan_email && (
                    <div style={{ display: 'flex', justifyContent: 'space-between', borderBottom: '1px solid #f1f5f9', paddingBottom: '0.4rem' }}>
                      <span style={{ color: '#64748b', fontWeight: 500 }}>FlyConan Email</span>
                      <strong>{result.flyconan_email}</strong>
                    </div>
                  )}
                  {result.linked_at && (
                    <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                      <span style={{ color: '#64748b', fontWeight: 500 }}>Linked At</span>
                      <span>{new Date(result.linked_at).toLocaleString()}</span>
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* Error State */}
          {!loading && !result?.success && (
            <div className="slide-in" style={{ marginBottom: '2rem' }}>
              <div
                className="message message-error"
                style={{
                  textAlign: 'left',
                  borderRadius: '16px',
                  padding: '1.5rem',
                }}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginBottom: '0.5rem' }}>
                  <i className="fas fa-exclamation-circle" style={{ fontSize: '1.4rem', color: '#dc2626' }}></i>
                  <h3 style={{ margin: 0, fontSize: '1.1rem', fontWeight: 700, color: '#991b1b' }}>
                    Account Linking Failed
                  </h3>
                </div>
                <p style={{ margin: 0, color: '#b91c1c', fontSize: '0.925rem', lineHeight: 1.5 }}>
                  {result?.error || 'Unable to verify FlyConan token or complete account association.'}
                </p>
              </div>
            </div>
          )}

          {/* Action Footer */}
          {!loading && (
            <div style={{ display: 'flex', gap: '1rem', justifyContent: 'center', flexWrap: 'wrap' }}>
              <button
                onClick={() => navigate('/')}
                className="btn btn-primary"
                style={{ padding: '0.85rem 2rem', fontSize: '0.95rem' }}
              >
                <i className="fas fa-th-large"></i>
                <span>Go to Dashboard</span>
              </button>

              <button
                onClick={() => navigate('/cases')}
                className="btn btn-secondary"
                style={{ padding: '0.85rem 1.75rem', fontSize: '0.95rem' }}
              >
                <i className="fas fa-folder-open"></i>
                <span>View Linked Cases</span>
              </button>
            </div>
          )}

        </div>
      </div>
    </div>
  );
};

export default FlyConanLinkAccount;

